import { TrashIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { type FC, useCallback, useEffect, useState } from 'react';
import { AvatarDisplay } from '@/app/components/avatar-display.tsx';
import GenericAlert, { type GenericAlertProps } from '@/app/components/generic-alert.tsx';
import { NoData } from '@/app/components/no-data.tsx';
import { OnboardImportStatusIndicator } from '@/app/components/onboarding/onboard-import-status-indicator.tsx';
import { Paginator, usePagination } from '@/app/components/paginator.tsx';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import toast from '@/app/toast.ts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from '@/primitives/table.tsx';
import { Text } from '@/primitives/text.tsx';
import {
  useDeleteOnboardImportMutation,
  useFetchAllOnboardImportsQuery,
} from '@/slices/onboard-import-api-slice.ts';
import type { OnboardImportListModel } from '../../types/onboard-import.type.ts';

const OnboardImportList: FC = () => {
  const { data, isLoading } = useFetchAllOnboardImportsQuery();
  const [deleteImport, { isLoading: deleteLoading, isError, isSuccess }] =
    useDeleteOnboardImportMutation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [alertProps, setAlertProps] = useState<GenericAlertProps>(null);

  const openDelete = (value: OnboardImportListModel) => {
    const alertProps = {
      onConfirm: () => {
        deleteImport(value.key);
        setDeleteOpen(false);
      },
    } as GenericAlertProps;

    setAlertProps(alertProps);
    setDeleteOpen(true);
  };

  const memoizedSortingDataAccessor = useCallback((value: OnboardImportListModel, col: string) => {
    switch (col) {
      case 'startedAt':
      case 'completedAt':
      case 'createdAt':
      case 'uploadedAt': {
        const date = DateTime.fromISO(_.get(value, col));
        return date.isValid ? date : '';
      }
      default:
        return _.get(value, col);
    }
  }, []);

  const { sort, setSort, sortData } = useSort({
    data,
    memoizedSortingDataAccessor,
  });
  const paginator = usePagination({ data: sortData });
  const { pagedData } = paginator;

  useEffect(() => {
    if (isError) {
      toast.error('There was an error attempting to delete an import.');
    }

    if (isSuccess) {
      toast.success('Import was deleted successfully.');
    }
  }, [isError, isSuccess]);
  return (
    <>
      <Table currentSort={sort} setCurrentSort={setSort}>
        <TableHead>
          <TableRow>
            <TableHeader sortColumn='onboardImportStatusType.description'>Status</TableHeader>
            <TableHeader sortColumn='uploadedAt'>Uploaded</TableHeader>
            <TableHeader sortColumn='startedAt'>Started</TableHeader>
            <TableHeader sortColumn='completedAt'>Completed</TableHeader>
            <TableHeader sortColumn='importedByUser.lastname'>Imported By</TableHeader>
            <TableHeader sortColumn='account.accountName'>Account</TableHeader>
            <TableHeader sortColumn='percentComplete'>Percent Complete</TableHeader>
            <TableHeader sortColumn='representativeCount'>Reps</TableHeader>
            <TableHeader sortColumn='staffMemberCount'>Staff Members</TableHeader>
            <TableHeader sortColumn='meetingCount'>Meetings</TableHeader>
            <TableHeader sortColumn='availabilitySlotCount'>Availability Slots</TableHeader>
            <TableHeader sortColumn='statusNotes'>Notes</TableHeader>
            <TableHeader></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedData?.length ? (
            pagedData.map(value => (
              <TableRow key={value.id} href={`/onboard-import/${value.key}`}>
                <TableCell className='flex'>
                  <OnboardImportStatusIndicator status={value.onboardImportStatusType} />
                </TableCell>
                <TableCell>{formatDate(value.uploadedAt, DateTime.DATETIME_SHORT, '')}</TableCell>
                <TableCell>{formatDate(value.startedAt, DateTime.DATETIME_SHORT, '')}</TableCell>
                <TableCell>{formatDate(value.completedAt, DateTime.DATETIME_SHORT, '')}</TableCell>
                <TableCell>
                  <div className='flex flex-row items-center gap-2'>
                    <AvatarDisplay
                      firstName={value.importedByUser.firstName}
                      lastName={value.importedByUser.lastName}
                      imageUrl={value.importedByUser.profilePhotoUrl}
                      className='size-6'
                    />
                    <Text>
                      {value.importedByUser.title ? `${value.importedByUser.title} ` : ''}
                      {value.importedByUser.firstName} {value.importedByUser.lastName}
                    </Text>
                  </div>
                </TableCell>
                <TableCell>{value.account.accountName}</TableCell>
                <TableCell>{Math.round(value.percentComplete)}%</TableCell>
                <TableCell>{value.representativeCount}</TableCell>
                <TableCell>{value.staffMemberCount}</TableCell>
                <TableCell>{value.meetingCount}</TableCell>
                <TableCell>{value.availabilitySlotCount}</TableCell>
                <TableCell>{value.statusNotes}</TableCell>
                <TableCell>
                  <Button
                    variant='light'
                    color='danger'
                    onClick={() => openDelete(value)}
                    title='Delete'
                    isIconOnly
                    isLoading={isLoading || deleteLoading}
                  >
                    <TrashIcon className='text-red-500' />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={12}>
                <NoData />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Paginator {...paginator} />
      <GenericAlert
        isOpen={deleteOpen}
        message='Are you sure you want to delete this import?'
        setIsOpen={setDeleteOpen}
        title='Delete Onboard Import'
        onConfirm={alertProps?.onConfirm}
      />
    </>
  );
};

export default OnboardImportList;
